import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useAffaireData } from './useAffaireData';
import {
  ExpertiseDetail,
  UseExpertiseDetailReturn,
} from '../types/expertise-detail.types';
import { getCompletionPercentage } from '../utils/expertiseStatusUtils';

// Helper function to format dates from FileMaker
// FM stores dates as varchar in various formats (DD/MM/YYYY, YYYY-MM-DD, etc.)
const formatDateForDisplay = (dateStr: string | null | undefined): string => {
  if (!dateStr) return '';

  // Try to parse the date string
  // FM format is typically DD/MM/YYYY or YYYY-MM-DD
  let date: Date | null = null;

  // Check if it's already in DD/MM/YYYY format
  if (/^\d{2}\/\d{2}\/\d{4}$/.test(dateStr)) {
    return dateStr; // Already in display format
  }

  // Try ISO format (YYYY-MM-DD)
  if (/^\d{4}-\d{2}-\d{2}/.test(dateStr)) {
    date = new Date(dateStr);
  } else {
    // Try to parse other formats
    date = new Date(dateStr);
  }

  if (date && !isNaN(date.getTime())) {
    return date.toLocaleDateString('fr-BE');
  }

  // Return original string if parsing fails
  return dateStr;
};

// Mock function to get expertise data - replace with actual API call
const fetchExpertiseData = async (dossierId: string): Promise<ExpertiseDetail | null> => {
  // This is a mock implementation
  // In production, this would fetch from your backend API

  // Simulate minimal API delay
  await new Promise(resolve => setTimeout(resolve, 100));

  // Mock data based on the dossierId - dates will be overridden with real data
  return {
    id: dossierId,
    affaire_id: dossierId,
    number: `EXP-${dossierId}`,
    type: 'Expertise immobilière',
    status: 'Visite programmée',
    currentStep: 3,
    createdDate: '', // Will be filled from affaireData
    scheduledDate: '', // Will be filled from affaireData
    completionPercentage: 35,
    client: {
      name: 'Jean Dupont',
      email: 'jean.dupont@email.com',
      phone: '+32 475 12 34 56',
      address: '12 rue des Lilas, 4000 Liège',
    },
    expert: {
      name: 'Marie Expert',
      email: 'marie.expert@chatelainxpert.be',
      phone: '+32 475 98 76 54',
    },
    price: '€ 850,00',
    reports: {
      intermediate: {
        available: false,
        fileName: null,
        date: null,
      },
      final: {
        available: false,
        fileName: null,
        date: null,
      },
    },
  };
};

export const useExpertiseDetail = (
  dossierId?: string,
  expertiseData?: ExpertiseDetail | null
): UseExpertiseDetailReturn => {
  const params = useParams<{ id: string }>();
  const affaireId = dossierId || params.id;
  
  const [expertise, setExpertise] = useState<ExpertiseDetail | null>(expertiseData || null);
  const [isLoading, setIsLoading] = useState(!expertiseData);
  const [error, setError] = useState<string | null>(null);

  // Use the hook to fetch affaire data
  const affaireData = useAffaireData(affaireId);

  // Get user role from auth context (mock for now)
  const getUserRole = (): string => {
    // In production, this would come from auth context
    // For now, we'll check localStorage or default to 'client'
    const user = localStorage.getItem('user');
    if (user) {
      try {
        const userData = JSON.parse(user);
        return userData.role || 'client';
      } catch {
        return 'client';
      }
    }
    return 'client';
  };

  const userRole = getUserRole();

  // Determine permissions based on role
  const canEdit = ['admin', 'bureau', 'expert'].includes(userRole);
  const canDelete = ['admin'].includes(userRole);

  useEffect(() => {
    const loadExpertiseData = async () => {
      if (!affaireId) {
        setIsLoading(false);
        setError('No dossier ID provided');
        return;
      }

      // Skip loading if we already have data
      if (expertise) {
        return;
      }

      setIsLoading(true);
      setError(null);

      try {
        // If expertise data is provided as prop, use it
        if (expertiseData) {
          setExpertise(expertiseData);
          setIsLoading(false);
        } else {
          // Otherwise fetch it
          const data = await fetchExpertiseData(affaireId);
          if (data) {
            // Enhance with real data from affaireData if available
            if (affaireData.contacts && affaireData.contacts.length > 0) {
              const mainContact = affaireData.contacts[0];
              data.client = {
                id: mainContact.k_id_contact,
                name: `${mainContact.prenom} ${mainContact.nom_representant}`,
                email: mainContact.email_1,
                phone: mainContact.tel_mobile,
                address: data.client.address, // Keep the mock address for now
              };
            }

            // Use real dates from affaireData.biens if available
            if (affaireData.biens && affaireData.biens.length > 0) {
              const mainBien = affaireData.biens[0];
              data.createdDate = formatDateForDisplay(mainBien.date_demande);
              data.scheduledDate = formatDateForDisplay(mainBien.date_du_rv);
            }

            // Update completion percentage based on status
            data.completionPercentage = getCompletionPercentage(data.status);
            data.currentStep = Math.floor(data.completionPercentage / 12.5) || 1;

            // Update report availability based on completion
            data.reports.intermediate.available = data.completionPercentage >= 70;
            data.reports.final.available = data.completionPercentage >= 100;

            setExpertise(data);
          } else {
            throw new Error('No expertise data found');
          }
        }
        setIsLoading(false);
      } catch (err) {
        console.error('Error loading expertise data:', err);
        setError(err instanceof Error ? err.message : 'Failed to load expertise data');
        setIsLoading(false);
      }
    };

    loadExpertiseData();
  }, [affaireId, expertiseData, affaireData.contacts, affaireData.biens]);

  return {
    expertise,
    affaireData,
    isLoading: isLoading || affaireData.loading,
    error: error || affaireData.error,
    canEdit,
    canDelete,
  };
};